import { css } from '@emotion/react';
import { useMemo } from 'react';

import { ScanDataMutators, useScanEditingContextSlice, useScanPartNotes } from '@kaiizen/kai-pro-api-react-state';
import {
  getEffectiveScanValue,
  getScanWorkStatusSummary,
  getTotalCostIfDefined,
  isGroupScoped,
  isLineItemScoped,
  isOptionScoped,
  isTaskScoped,
  ScanGroupOptimized,
  ScanTaskOptimized,
} from '@kaiizen/kai-scan-template-system';
import { Button } from '../../../Widgets';

import { formatUsd } from '@/Client/Presenters';
import { useNavigateToCloseoutScopePreview, useNavigateToPunchlistScopePreview, useNavigateToScope } from '@/Client/State';
import { groupNamePresenter, taskNamePresenter } from '../../../Presenters';
import { COLORS2, LAYOUT2, TYPOGRAPHY2 } from '../../../Styles';
import { ScanPartNotesButton } from '../ScanPartNotesButton';

const getNextAndPreviousInList = <T extends unknown>(list: T[], current: T) => {
  const index = list.indexOf(current);
  if (index === -1) return { next: list[0], previous: list[0] };
  return { next: list[(index + 1) % list.length], previous: list[(index - 1 + list.length) % list.length] };
};

export type ProgressWalkEditorProps = {
  scanId: string;
  group: ScanGroupOptimized;
  groups: ScanGroupOptimized[];
  task: ScanTaskOptimized;
  tasks: ScanTaskOptimized[];
  baselineGroup: ScanGroupOptimized;
  baselineTask: ScanTaskOptimized;
  scanDataMutators: ScanDataMutators;
  setCurrentGroupIdAndTaskId: (groupId: string | undefined, taskId: string | undefined) => void;
};

const HEADER_STYLE = css`
  label: HEADER_STYLE;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: ${LAYOUT2.spacing1}px;
  padding: ${LAYOUT2.spacing1}px;
`;

const MAIN_SECTION_STYLE = css`
  label: MAIN_SECTION_STYLE;
  display: flex;
  ${TYPOGRAPHY2.small};
  flex-direction: column;
  flex: 1;
`;

const PRIMARY_SECTION_TITLE_STYLE = css`
  label: CENTER_GROUP_NAME_STYLE;
  ${TYPOGRAPHY2.mediumBold};
  color: var(--text);
`;

const SECONDARY_SECTION_STYLE = css`
  label: SECONDARY_SECTION_VALUE_STYLE;
  ${TYPOGRAPHY2.xSmall};
  display: flex;
  color: var(--text);
  flex-direction: column;
  flex: 1;
`;

const SECONDARY_SECTION_TITLE_STYLE = css`
  label: CENTER_GROUP_NAME_STYLE;
  ${TYPOGRAPHY2.smallBold};
  color: var(--text);
`;

const NAV_BUTTONS_COLUMN_STYLE = css`
  label: NAV_BUTTONS_COLUMN_STYLE;
  display: flex;
  flex-direction: column;
  gap: ${LAYOUT2.spacingHalf}px;
`;

const NAV_BUTTONS_FIRST_ROW_STYLE = css`
  label: NAV_BUTTONS_FIRST_ROW_STYLE;
  display: flex;
  gap: ${LAYOUT2.spacingHalf}px;
`;

const LINE_ITEM_VALUE_STYLE = css`
  label: LINE_ITEM_VALUE_STYLE;
  ${TYPOGRAPHY2.xSmallBold};
  color: var(--text);
`;

const PreviousNextIcon = ({ direction, color = COLORS2.alwaysDark.green }: { direction: 'previous' | 'next'; color?: string }) => (
  <svg width='14' height='27' viewBox='0 0 14 27' fill='none'>
    <path
      d='M12.8417 0.999999L6.92084 7.125L1.00001 13.25L6.92084 19.375L12.8417 25.5'
      stroke={color}
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
      transform={direction === 'next' ? 'rotate(180 7 13.5)' : ''}
    />
  </svg>
);

type GroupTaskPair = { group: ScanGroupOptimized; task?: ScanTaskOptimized };

const getCombinedGroupTaskList = (
  groups: ScanGroupOptimized[],
  tasks: ScanTaskOptimized[],
  currentGroup?: ScanGroupOptimized,
  currentTask?: ScanTaskOptimized,
) => {
  if (!currentGroup) return { groupTaskList: [], currentGroupTaskPair: undefined };
  const groupTaskList: GroupTaskPair[] = [];
  let currentGroupTaskPair: GroupTaskPair | undefined = undefined;
  for (const group of groups) {
    if (!isGroupScoped(group)) continue;
    if (!currentTask) {
      const pair = { group };
      groupTaskList.push(pair);
      if (currentGroup && group.id === currentGroup.id) {
        currentGroupTaskPair = pair;
      }
      continue;
    }

    for (const task of group.tasks) {
      if (!isTaskScoped(task)) continue;
      const pair = { group, task };
      if (task.id === currentTask.id && group.id === currentGroup.id) {
        currentGroupTaskPair = pair;
      }
      groupTaskList.push(pair);
    }
  }
  return { groupTaskList, currentGroupTaskPair };
};

export const ProgressHeader = ({ group, groups, task, tasks, setCurrentGroupIdAndTaskId, scanId }: {
  scanId: string;
  group?: ScanGroupOptimized;
  task?: ScanTaskOptimized;
  groups: ScanGroupOptimized[];
  tasks: ScanTaskOptimized[];
  setCurrentGroupIdAndTaskId: (groupId: string | undefined, taskId: string | undefined) => void;
}) => {
  const navigateToPunchlistScopePreview = useNavigateToPunchlistScopePreview();
  const navigateToCloseoutScopePreview = useNavigateToCloseoutScopePreview();
  const navigateToScope = useNavigateToScope();
  const { groupTaskList, currentGroupTaskPair } = useMemo(() => getCombinedGroupTaskList(groups, tasks, group, task), [groups, tasks, group, task]);
  const { getContractorForTask, scanRoot } = useScanEditingContextSlice(['getContractorForTask', 'scanRoot']);
  const { scanPartNotes: scanNotes } = useScanPartNotes(task?.id ?? group?.id);

  const contractor = task ? getContractorForTask(task) ?? { name: task ? 'Unassigned' : 'No contractor assigned' } : undefined;

  const { next: nextGroupTaskPair, previous: previousGroupTaskPair } = getNextAndPreviousInList(groupTaskList, currentGroupTaskPair);
  console.log({ currentGroupTaskPair, nextGroupTaskPair, previousGroupTaskPair });

  const scanWorkStatus = useMemo(() => scanRoot ? getScanWorkStatusSummary(scanRoot) : undefined, [scanRoot]);
  const scanNeedsRework = scanWorkStatus?.workStatus === 'needsRework';
  const scanIsCompleted = scanWorkStatus?.workStatus === 'completed';

  const options = useMemo(() => task?.options?.filter(isOptionScoped), [task?.options]);
  const lineItems = useMemo(() => options?.flatMap(option => option.lineItems).filter(isLineItemScoped), [options]);

  if (!group) return null;

  return (
    <div css={HEADER_STYLE}>
      <div css={MAIN_SECTION_STYLE}>
        <span css={PRIMARY_SECTION_TITLE_STYLE}>
          {task ? 'Progress: ' + taskNamePresenter(task) + ' Task' : 'Progress: ' + groupNamePresenter(group) + ' Group'}
        </span>
        {contractor ? <span>Contractor: {contractor.name}</span> : null}
        {options?.map(option => <span key={option.id}>Resolution: {option.template.name}</span>)}
      </div>
      {task
        ? (
          <div css={SECONDARY_SECTION_STYLE}>
            <span css={SECONDARY_SECTION_TITLE_STYLE}>Line Items</span>
            {lineItems?.map(lineItem => {
              const { quantity, price, laborPrice, materialCost, laborCost } = lineItem.values;
              const totalCost = getTotalCostIfDefined(lineItem);
              const showQuantity = getEffectiveScanValue(quantity) !== 1;
              const haveBothCosts = price && laborPrice;
              const haveBothValues = materialCost && laborCost;
              return (
                <span key={lineItem.id}>
                  {lineItem.template.name} {haveBothCosts
                    ? (
                      <>
                        (
                        <span css={LINE_ITEM_VALUE_STYLE}>{formatUsd(getEffectiveScanValue(price))}</span>
                        +
                        <span css={LINE_ITEM_VALUE_STYLE}>{formatUsd(getEffectiveScanValue(laborPrice))}</span>
                        )
                      </>
                    )
                    : <span css={LINE_ITEM_VALUE_STYLE}>{formatUsd(getEffectiveScanValue(price || laborPrice))}</span>}
                  {showQuantity
                    ? (
                      <>
                        x <span css={LINE_ITEM_VALUE_STYLE}>{getEffectiveScanValue(quantity)}</span>
                      </>
                    )
                    : null}
                  {showQuantity || haveBothValues
                    ? (
                      <>
                        {' x '}
                        <span css={LINE_ITEM_VALUE_STYLE}>{formatUsd(totalCost ?? 0)}</span>
                      </>
                    )
                    : null}
                </span>
              );
            })}
          </div>
        )
        : null}

      <ScanPartNotesButton
        scanPart={task ?? group}
        notes={scanNotes}
      />
      {/* <Button buttonStyle={debugReset ? "yellow" : "gray"} onClick={debugReset}>D</Button> */}

      <div css={NAV_BUTTONS_COLUMN_STYLE}>
        <div css={NAV_BUTTONS_FIRST_ROW_STYLE}>
          <Button onClick={() => previousGroupTaskPair && setCurrentGroupIdAndTaskId(previousGroupTaskPair.group.id, previousGroupTaskPair.task?.id)}>
            <PreviousNextIcon color={COLORS2.alwaysDark.white} direction='previous' />last
          </Button>
          <Button onClick={() => nextGroupTaskPair && setCurrentGroupIdAndTaskId(nextGroupTaskPair.group.id, nextGroupTaskPair.task?.id)}>
            next<PreviousNextIcon color={COLORS2.alwaysDark.white} direction='next' />
          </Button>
        </div>
        <Button
          onClick={() =>
            scanNeedsRework
              ? navigateToPunchlistScopePreview({ scanId })
              : scanIsCompleted
              ? navigateToCloseoutScopePreview({ scanId })
              : navigateToScope({ scanId })}
        >
          {scanNeedsRework
            ? 'view punch list'
            : scanIsCompleted
            ? 'view close out'
            : 'view scope'}
        </Button>
      </div>
    </div>
  );
};
